import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Pagination,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import {
    createAdminCreditDebit,
    createAdminCreditTopup,
    getUserBillingType,
    listAdminBillingTransactions,
    listAdminCreditTopups,
    updateUserBillingType,
} from "../../../api/superAdminApi";
import useNotification from "../../common/Notification";
import {
    formatCreditCount,
    formatRupeesFromPaisa,
    formatWhen,
    rupeesToPaisa,
    serviceLabel,
    SERVICE_LABELS,
} from "../../billing/billingFormat";

const TYPE_OPTIONS = [
    { value: "", label: "Not set (fixed quotas)" },
    { value: "FIXED_BASED", label: "Fixed plan" },
    { value: "CREDIT_BASED", label: "Credit wallet" },
];

const AdminUserBillingPage = () => {
    const notify = useNotification();
    const { userId } = useOutletContext();
    const [billing, setBilling] = useState(null);
    const [billingType, setBillingType] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [confirmType, setConfirmType] = useState(null);
    const [savingType, setSavingType] = useState(false);
    const [amount, setAmount] = useState("");
    const [adjustMode, setAdjustMode] = useState(null);
    const [adjusting, setAdjusting] = useState(false);
    const [tab, setTab] = useState(0);
    const [filters, setFilters] = useState({ service: "", transaction_type: "", from: "", to: "" });
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [lastPage, setLastPage] = useState(1);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState("");

    const loadBilling = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await getUserBillingType(userId);
            setBilling(data || {});
            setBillingType(data?.billing_type || "");
        } catch (err) {
            setError(err.message || "Could not load billing");
        } finally {
            setLoading(false);
        }
    }, [userId]);

    const loadList = useCallback(async () => {
        setListLoading(true);
        setListError("");
        try {
            const params = { page, per_page: 20 };
            if (tab === 0) {
                if (filters.service) params.service = filters.service;
                if (filters.transaction_type) params.transaction_type = filters.transaction_type;
                if (filters.from) params.from = filters.from;
                if (filters.to) params.to = filters.to;
            }
            const result = tab === 0
                ? await listAdminBillingTransactions(userId, params)
                : await listAdminCreditTopups(userId, params);
            setRows(result.rows);
            setLastPage(result.lastPage);
        } catch (err) {
            setRows([]);
            setListError(err.message || "Could not load history");
        } finally {
            setListLoading(false);
        }
    }, [filters, page, tab, userId]);

    useEffect(() => { loadBilling(); }, [loadBilling]);
    useEffect(() => { if (!loading) loadList(); }, [loadList, loading]);

    const saveType = async () => {
        setSavingType(true);
        try {
            await updateUserBillingType(userId, confirmType === "" ? null : confirmType);
            notify("Billing type saved. Subscription dates and quota balances were not changed.", "success");
            setConfirmType(null);
            loadBilling();
        } catch (err) {
            notify(err.message || "Could not update billing type", "error");
        } finally {
            setSavingType(false);
        }
    };

    const applyAdjustment = async () => {
        const credits = rupeesToPaisa(amount);
        if (credits == null || credits <= 0) {
            notify("Enter an amount greater than zero", "error");
            return;
        }
        setAdjusting(true);
        try {
            if (adjustMode === "topup") await createAdminCreditTopup(userId, credits);
            else await createAdminCreditDebit(userId, credits);
            notify(
                adjustMode === "topup"
                    ? `${formatCreditCount(credits)} added`
                    : `${formatCreditCount(credits)} removed`,
                "success"
            );
            setAmount("");
            setAdjustMode(null);
            loadBilling();
            loadList();
        } catch (err) {
            notify(
                err.status === 402 ? "The balance is too low for this debit." : (err.message || "Could not update the wallet"),
                "error"
            );
        } finally {
            setAdjusting(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} /></Box>;
    }

    const creditsPreview = rupeesToPaisa(amount);

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Alert severity="info" sx={{ mb: 2 }}>
                Changing the billing type does not change subscription end dates or the existing quota columns.
            </Alert>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end", mb: 2 }}>
                <TextField
                    select
                    size="small"
                    label="Billing type"
                    value={billingType}
                    onChange={(e) => setBillingType(e.target.value)}
                    sx={{ minWidth: 240 }}
                >
                    {TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.label} value={option.value}>{option.label}</MenuItem>
                    ))}
                </TextField>
                <Button
                    variant="contained"
                    onClick={() => setConfirmType(billingType)}
                    disabled={(billing?.billing_type || "") === billingType}
                >
                    Save billing type
                </Button>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {formatRupeesFromPaisa(billing?.current_credit)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {formatCreditCount(billing?.current_credit)} on this wallet. 1 credit = 1 paisa.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
                <Button variant="outlined" onClick={() => setAdjustMode("topup")}>Add credits</Button>
                <Button variant="outlined" color="error" onClick={() => setAdjustMode("debit")}>Remove credits</Button>
            </Box>

            <Tabs value={tab} onChange={(_, value) => { setTab(value); setPage(1); }} sx={{ mb: 2 }}>
                <Tab label="Transactions" />
                <Tab label="Top-ups" />
            </Tabs>
            {tab === 0 && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                    <TextField select size="small" label="Service" value={filters.service} onChange={(e) => { setFilters((f) => ({ ...f, service: e.target.value })); setPage(1); }} sx={{ minWidth: 180 }}>
                        <MenuItem value="">All</MenuItem>
                        {Object.keys(SERVICE_LABELS).map((key) => (
                            <MenuItem key={key} value={key}>{serviceLabel(key)}</MenuItem>
                        ))}
                    </TextField>
                    <TextField select size="small" label="Type" value={filters.transaction_type} onChange={(e) => { setFilters((f) => ({ ...f, transaction_type: e.target.value })); setPage(1); }} sx={{ minWidth: 140 }}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="CREDIT">Credit in</MenuItem>
                        <MenuItem value="DEBIT">Credit out</MenuItem>
                    </TextField>
                    <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.from} onChange={(e) => { setFilters((f) => ({ ...f, from: e.target.value })); setPage(1); }} />
                    <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.to} onChange={(e) => { setFilters((f) => ({ ...f, to: e.target.value })); setPage(1); }} />
                </Box>
            )}
            {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}
            {listLoading ? (
                <CircularProgress size={24} />
            ) : rows.length === 0 ? (
                <Alert severity="info">{tab === 0 ? "No transactions for this account or its sub-users." : "No top-ups for this account."}</Alert>
            ) : (
                <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {(tab === 0 ? ["When", "User", "Service", "Type", "Credits"] : ["When", "Amount", "Mode", "Status"]).map((heading) => (
                                    <TableCell key={heading} sx={{ fontWeight: 700 }}>{heading}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                tab === 0 ? (
                                    <TableRow key={row.id}>
                                        <TableCell>{formatWhen(row.created_at)}</TableCell>
                                        <TableCell>{row.user?.name || row.user_id || "—"}</TableCell>
                                        <TableCell>{serviceLabel(row.service, row.template_category)}</TableCell>
                                        <TableCell>{row.transaction_type === "CREDIT" ? "Credit in" : "Credit out"}</TableCell>
                                        <TableCell>{formatCreditCount(row.credit_spent)}</TableCell>
                                    </TableRow>
                                ) : (
                                    <TableRow key={row.id}>
                                        <TableCell>{formatWhen(row.created_at)}</TableCell>
                                        <TableCell>{formatRupeesFromPaisa(row.credit_amount)}</TableCell>
                                        <TableCell>{row.payment_mode === "ADMIN" ? "Added by system" : "Gateway"}</TableCell>
                                        <TableCell>{row.payment_status || "—"}</TableCell>
                                    </TableRow>
                                )
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
            {lastPage > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Pagination count={lastPage} page={page} onChange={(_, value) => setPage(value)} />
                </Box>
            )}

            <Dialog open={confirmType != null} onClose={() => !savingType && setConfirmType(null)}>
                <DialogTitle>Save billing type</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Set this account to {TYPE_OPTIONS.find((option) => option.value === confirmType)?.label}. Subscription end dates and quota balances stay as they are.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmType(null)} disabled={savingType}>Cancel</Button>
                    <Button variant="contained" onClick={saveType} disabled={savingType}>{savingType ? "Saving…" : "Confirm"}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(adjustMode)} onClose={() => !adjusting && setAdjustMode(null)}>
                <DialogTitle>{adjustMode === "debit" ? "Remove credits" : "Add credits"}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        type="number"
                        label="Amount (₹)"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        helperText={creditsPreview > 0 ? `${formatCreditCount(creditsPreview)} will be ${adjustMode === "debit" ? "removed" : "added"}` : "1 credit = 1 paisa"}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAdjustMode(null)} disabled={adjusting}>Cancel</Button>
                    <Button variant="contained" color={adjustMode === "debit" ? "error" : "primary"} onClick={applyAdjustment} disabled={adjusting}>
                        {adjusting ? "Saving…" : "Confirm"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminUserBillingPage;
